import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Missing reset token — open the link in your email again.");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password needs to be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      localStorage.setItem("fairway_token", data.token);
      await refresh();
      setDone(true);
      setTimeout(() => nav("/app", { replace: true }), 1500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-shell min-h-screen p-6 flex flex-col">
      <Link to="/login" className="btn-ghost self-start -ml-2">
        <ArrowLeft size={20} weight="bold" /> Sign in
      </Link>
      <div className="flex items-center mt-6 mb-2" data-testid="reset-logo">
        <img src="/fairway-logo.png" alt="Fairway" className="h-16 w-auto rounded-2xl" />
      </div>
      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mt-6 mb-2">
        Set a new password
      </h1>
      <p className="text-[var(--text-secondary)] mb-8">Choose a fresh password — 6+ characters.</p>

      {done ? (
        <div className="surface-card p-6 text-center" data-testid="reset-success">
          <div
            className="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center"
            style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
          >
            <CheckCircle size={32} weight="fill" />
          </div>
          <h2 className="font-heading font-bold text-xl text-[var(--primary)] mb-2">Password updated</h2>
          <p className="text-sm text-[var(--text-secondary)]">Signing you in…</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="reset-password-form">
          <div>
            <label className="label-uppercase block mb-2">New password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              minLength={6}
              className="input-field"
              data-testid="reset-password-input"
            />
          </div>
          <div>
            <label className="label-uppercase block mb-2">Confirm password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              className="input-field"
              data-testid="reset-confirm-input"
            />
          </div>
          {error && (
            <div
              data-testid="reset-error"
              className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-4 py-3"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            className="btn-primary mt-2"
            disabled={submitting || !token}
            data-testid="reset-submit-button"
          >
            {submitting ? "Updating..." : "Set new password"}
          </button>
        </form>
      )}
    </div>
  );
}
